import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { OBJECT_TYPE } from "common/constants";
import { buildLink } from "common/routing";
import { getDisclaimerText } from "common-client/utils/disclaimerOverrides";
import {
  DisclaimerName,
  useGetResidentCustomObjectDocumentsQuery,
} from "../../../generated/graphql";
import DocumentUploadsList from "../../AddressPanel/DocumentUploads/List";
import DocumentUploadsListItem from "../../AddressPanel/DocumentUploads/ListItem";
import { AuthContext } from "../../Authorization/AuthContext";
import Disclaimer from "../../Common/Disclaimer";
import Icon, { ICON_COLORS, Icons } from "../../Common/Icons";
import {
  LoadingContainer,
  TabContainer,
} from "../Property/__styles__/PropertyPage";

interface Props {
  geometryId: string;
  geometryLabel: string;
  customMapId: string;
}

export const Files = ({ geometryId, geometryLabel, customMapId }: Props) => {
  const { account } = useContext(AuthContext);
  const { t } = useTranslation();

  const { data, loading } = useGetResidentCustomObjectDocumentsQuery({
    variables: { geometryId, isPublic: true },
  });

  const documentUploads = data?.customMapGeometry?.documentUploads ?? [];

  const disclaimerText = getDisclaimerText({
    disclaimerOverrides: account?.disclaimerOverrides,
    name: DisclaimerName.DOCUMENTS,
    isPublic: true,
    defaultDisclaimers: {
      public: t("disclaimers-documents", {
        accountName: account?.name || "",
        getHelpLink: buildLink("getHelp", { accountId: account?.id || "" }),
      }),
    },
  });

  if (loading) {
    return (
      <LoadingContainer>
        <Icon icon={Icons.LOADING} color={ICON_COLORS.LIGHT_GREY} />
      </LoadingContainer>
    );
  }

  return (
    <TabContainer data-testid="documents">
      <DocumentUploadsList
        object={{
          id: geometryId,
          objectType: OBJECT_TYPE.CUSTOM_MAP_GEOMETRY,
          objectLabel: geometryLabel,
          customMapId,
        }}
      >
        {documentUploads.map(documentUpload => (
          <div key={documentUpload.id}>
            <DocumentUploadsListItem
              documentUpload={{
                ...documentUpload,
                hiddenFromPublic: false,
                isDeletable: false,
              }}
            />
          </div>
        ))}
      </DocumentUploadsList>
      <Disclaimer message={disclaimerText} />
    </TabContainer>
  );
};
